import useScrollAnimation from '../../hooks/useScrollAnimation';

interface AreaGroup {
  state: string;
  cities: string[];
}

interface ServiceAreasSectionProps {
  eyebrow?: string;
  headline: string;
  subheadline?: string;
  areas: AreaGroup[];
  showMap?: boolean;
}

const ServiceAreasSection = ({
  eyebrow = 'Where We Work',
  headline,
  subheadline,
  areas,
  showMap = true,
}: ServiceAreasSectionProps) => {
  const { ref, isVisible } = useScrollAnimation();

  return (
    <section className="bg-[#f7f4ef] py-24 lg:py-32 overflow-hidden">
      <div className="max-w-screen-xl mx-auto px-6 lg:px-10">
        <div ref={ref} className={`anim-section ${isVisible ? 'is-visible' : ''} mb-14`}>
          {eyebrow && (
            <p className="text-[#c8a951] text-xs uppercase tracking-[0.35em] font-semibold mb-4">{eyebrow}</p>
          )}
          <h2
            className="font-display text-4xl lg:text-5xl font-bold text-[#0a0a0a] mb-4"
            style={{ fontFamily: '"Playfair Display", serif' }}
          >
            {headline}
          </h2>
          {subheadline && <p className="text-[#0a0a0a]/50 text-base max-w-xl leading-relaxed">{subheadline}</p>}
        </div>

        <div className={`flex flex-col ${showMap ? 'lg:flex-row' : ''} gap-10`}>
          {/* Map */}
          {showMap && (
            <div className="lg:w-[55%] h-[440px] overflow-hidden">
              <iframe
                title="Polished Concrete Plus Location"
                src="https://maps.google.com/maps?width=100%25&height=500&hl=en&q=Polished+Concrete+Plus+15076+15th+Ave+NW+Rice+MN+56367&t=&z=9&ie=UTF8&iwloc=B&output=embed"
                className="w-full h-full border-0"
                loading="lazy"
                referrerPolicy="no-referrer-when-downgrade"
              />
            </div>
          )}

          {/* Areas list */}
          <div className={`${showMap ? 'lg:w-[45%]' : 'w-full'}`}>
            <div className={`grid grid-cols-1 ${showMap ? '' : 'md:grid-cols-3'} gap-8`}>
              {areas.map((group, i) => (
                <AreaGroupBlock key={i} group={group} index={i} />
              ))}
            </div>

            <div className="mt-10 pt-8 border-t border-[#0a0a0a]/10">
              <p className="text-[#0a0a0a]/50 text-sm leading-relaxed">
                <strong className="text-[#0a0a0a]">Headquartered in Sauk Rapids, MN.</strong><br />
                We travel across Minnesota and into Iowa, Wisconsin, North Dakota, and South Dakota
                for residential, commercial, and industrial projects.
              </p>
              <a
                href="tel:3204931454"
                className="inline-flex items-center gap-2 mt-5 text-[#c8a951] text-sm font-semibold hover:text-[#0a0a0a] transition-colors cursor-pointer"
              >
                <i className="ri-phone-line" /> Call 320.493.1454 — We'll come to you
              </a>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

const AreaGroupBlock = ({ group, index }: { group: AreaGroup; index: number }) => {
  const { ref, isVisible } = useScrollAnimation();
  return (
    <div
      ref={ref}
      className={`anim-section ${isVisible ? 'is-visible' : ''}`}
      style={{ transitionDelay: `${index * 0.1}s` }}
    >
      <h3
        className="font-display text-lg font-bold text-[#0a0a0a] mb-4 pb-3 border-b border-[#c8a951]/30"
        style={{ fontFamily: '"Playfair Display", serif' }}
      >
        {group.state}
      </h3>
      <ul className="space-y-2">
        {group.cities.map((city, j) => (
          <li key={j} className="flex items-center gap-2 text-sm text-[#0a0a0a]/65">
            <span className="w-1.5 h-1.5 bg-[#c8a951] shrink-0" />
            {city}
          </li>
        ))}
      </ul>
    </div>
  );
};

export default ServiceAreasSection;
